import React, { useEffect, useMemo, useState } from 'react';
import * as Alert from '@radix-ui/react-alert-dialog';
import InfoTooltip from '../InfoTooltip';
import OtpInput from './OtpInput';

/**
 * MfaDialog — shared dialog for both phone/SMS and TOTP MFA verification.
 *
 * The dialog adapts its messaging and available actions based on the `mfaType` prop:
 *
 *   - 'phone' (default): Shows "enter the code sent to your phone", with a
 *     Resend button and countdown timer. This is the existing behaviour for
 *     all human users who enrolled in SMS MFA.
 *
 *   - 'totp': Shows "enter the code from your authenticator app". No Resend
 *     button (TOTP codes are generated locally, not sent by us). Different
 *     tooltip text. This mode is used by the RPA bot service account which
 *     enrolls in TOTP MFA to avoid reCAPTCHA.
 *
 * The code input, verification, and error handling are identical for both modes.
 */
export default function MfaDialog({
  open,
  onOpenChange,
  mfaType = 'phone', // 'phone' | 'totp' — controls messaging and resend availability
  maskedPhone,
  isLoading = false,
  error = '',
  errorCode = '',
  onVerify, // (code: string) => Promise<void>
  onCancel, // () => void
  onResend, // optional: () => Promise<{ ok: boolean }> — only shown for phone MFA
  sentAt = 0,
  cooldownSeconds = 30,
  tooltipSide = 'right',
  tooltipContent
}) {
  const [verificationCode, setVerificationCode] = useState('');
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [resendJustSent, setResendJustSent] = useState(false);

  // Determine if we're in TOTP mode (affects UI messaging and resend availability)
  const isTotp = mfaType === 'totp';

  // Compute last4 safely from maskedPhone (only relevant for phone MFA)
  const last4 = useMemo(() => (maskedPhone || ''), [maskedPhone]);

  // Reset input whenever dialog closes
  useEffect(() => {
    if (!open) {
      setVerificationCode('');
      setResendJustSent(false);
    }
  }, [open]);

  // Countdown driven by sentAt (only relevant for phone MFA)
  useEffect(() => {
    if (isTotp) return; // No countdown for TOTP
    const update = () => {
      if (sentAt) {
        const elapsed = Math.floor((Date.now() - sentAt) / 1000);
        setResendSecondsLeft(Math.max(0, cooldownSeconds - elapsed));
      } else {
        setResendSecondsLeft(0);
      }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [sentAt, cooldownSeconds, isTotp]);

  const handleResend = async () => {
    if (!onResend || isTotp) return;
    const res = await onResend();
    if (res?.ok) {
      setResendJustSent(true);
      setTimeout(() => setResendJustSent(false), 1500);
    }
  };

  const handleCancel = () => {
    setVerificationCode('');
    setResendJustSent(false);
    onCancel && onCancel();
    onOpenChange && onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((verificationCode || '').length !== 6) return;
    await onVerify(verificationCode);
  };

  // Default tooltip content varies by MFA type
  const defaultPhoneTooltip =
    "Having trouble receiving the code?\n\nDelays can happen due to your phone carrier or network issues.\n\nYou can try:\n\n→ Clearing your browser's cache and trying again\n\n→ Using a different browser (like Chrome, Firefox, or Edge)\n\n→ Trying to log in using incognito/private browsing mode\n\nIf the problem continues, please contact your organisation admin for help.";
  const defaultTotpTooltip =
    "Enter the 6-digit code shown in your authenticator app.\n\nCodes refresh every 30 seconds. Make sure you enter the current code before it expires.\n\nIf you're having trouble, ensure the time on your device is set to automatic (synced with the network).\n\nIf the problem continues, please contact your organisation admin for help.";

  // Description text shown below the dialog title
  const descriptionText = isTotp
    ? 'Please enter the code from your authenticator app.'
    : `Please enter the code sent to your phone number${last4 ? ` ending in ${last4}` : ''}.`;

  return (
    <Alert.Root open={open} onOpenChange={onOpenChange}>
      <Alert.Portal>
        <Alert.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-[200]" />
        <Alert.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[90vw] max-w-[500px] shadow-2xl ring-1 ring-black/5 z-[200]">
          <div className="flex items-center justify-between mb-2">
            <Alert.Title className="text-xl font-bold">
              Two-Factor Authentication
            </Alert.Title>
            <InfoTooltip
              side={tooltipSide}
              content={tooltipContent || (isTotp ? defaultTotpTooltip : defaultPhoneTooltip)}
            />
          </div>
          <Alert.Description className="mb-2">
            {descriptionText}
          </Alert.Description>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <OtpInput
                value={verificationCode}
                onChange={setVerificationCode}
                disabled={isLoading}
                autoFocus
              />
            </div>

            <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mb-4' : 'max-h-0 mb-0'}`}>
              <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                {error}
                {!!errorCode && (
                  <div className="text-xs mt-1 text-gray-600">Error code: {errorCode}</div>
                )}
              </div>
            </div>

            <div className="font-semibold flex justify-end gap-4">
              {/* Hide Cancel and Resend buttons while verification is in flight
                  to prevent mid-check cancellations or duplicate resend requests. */}
              {!isLoading && (
                <Alert.Cancel asChild>
                  <button className="px-4 py-2 text-gray-500 hover:text-gray-700" onClick={handleCancel}>
                    Cancel
                  </button>
                </Alert.Cancel>
              )}
              {/* Resend button is only shown for phone/SMS MFA — TOTP codes are
                  generated locally by the authenticator app, so there's nothing to resend. */}
              {!!onResend && !isTotp && !isLoading && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendSecondsLeft > 0}
                  className={`px-4 py-2 rounded-lg border ${resendJustSent ? 'border-green-600 bg-green-50' : 'border-primary hover:bg-primary/5'} text-primary disabled:text-gray-400 disabled:border-gray-300 disabled:bg-transparent relative`}
                >
                  <span className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${resendJustSent ? 'opacity-100' : 'opacity-0'}`}>
                    ✔ Code sent
                  </span>
                  <span className={`transition-opacity duration-300 ${resendJustSent ? 'opacity-0' : 'opacity-100'}`}>
                    {`Resend Code${resendSecondsLeft > 0 ? ` (${resendSecondsLeft}s)` : ''}`}
                  </span>
                </button>
              )}
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-primary/40 disabled:cursor-not-allowed"
                disabled={(verificationCode?.length || 0) !== 6 || isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Verify Code'
                )}
              </button>
            </div>
          </form>
        </Alert.Content>
      </Alert.Portal>
    </Alert.Root>
  );
}
